/* Print — the StudioCore desktop Print Preview, feature for feature (inventory:
 * app/specifications/studioCloudV2/todo/brightivity-web/m3-connect-and-print.md "Desktop parity"), in the
 * Brightivity kit. Left: Print Queue cards (Portrait / Landscape / Custom, Print, Nest, delete). Centre:
 * Quality, Vertical trimmer, ink estimate, the preview or the nest canvas. Right: Printers, Print jobs,
 * Print settings (Copies, Nesting), Ink levels. Real printing through Brightivity Helper. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopBar, SUPPLY_STORE_URL } from '../app/AppShell.jsx';
import { helperDownloadUrl, browserOs, runCommand, uploadFile } from '../api/bridge.js';
import { useDevices, useLocalComputer } from '../features/computers/computers.jsx';

import {
  DEFAULT_QUALITY, QUALITY, channelDot, customCanvas, customError, customLimits, defaultCustom, estimatorQuality,
  dedupeSources, family, paperGeom, printPayload, printSize, sizesOriginal, sourceValue, statusPill,
} from '../features/print/printRules.js';
import { Button, Card, EmptyState, Icon, Modal, Select, Spinner } from '../ui/index.jsx';
import { takeHandedFiles } from '../features/print/handoff.js';
import { ingestForApproval, schoolMe, submitForApproval, uploadForApproval } from '../api/school.js';
import { makeThumbnail } from '../api/library.js';
import { clearNest, usePrintStore } from '../features/print/PrintStore.jsx';
import { DropZone, ItemCard, NestThumb, Preview } from '../features/print/cards.jsx';
import { InkBox, PrintersBox, PrintJobsBox, PrintSettingsBox, usePersistedOpen } from '../features/print/panel.jsx';
import NestCanvas from '../features/print/nest/NestCanvas.jsx';
import {
  NEST_AUTO_MIN_IN, NEST_MIN_ITEM_IN, arrangeEqual, arrangeTight, autoEqualWidths, findFreeSpot, placedAspect, printOrder,
  stripHeightOf, stripWidthFor, visibleFill,
} from '../features/print/nest/nestLayout.js';
import { composeNest, nestThumbnail } from '../features/print/nest/composeNest.js';
import '../features/print/print.css';
import { useTranslation } from 'react-i18next';

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
};
const fmt = (n, d = 2) => (Number.isFinite(n) ? Number(n).toFixed(d) : '–');
const r3 = (v) => Math.round(v * 1000) / 1000;
const r2 = (v) => Math.round(v * 100) / 100;
const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const modelRollWidthIn = (model) => { const m = String(model || '').match(/(\d{2})-in/i); return m ? Number(m[1]) : null; };

/** Centre ink pill (desktop ink estimate): channels, total + cost, copies line, print-queue line. */
function InkPill({ est, copies, pending }) {
  if (!est || est.unsupported || est.error) return null;
  if (est.loading) return <div className="bv-inkchip" aria-busy="true"><Spinner label="Estimating ink" /><span className="bv-muted">Estimating...</span></div>;
  const d = est.data;
  if (!d) return null;
  const ch = Object.entries(d.per_channel_ml || {});
  const ml = Number(d.total_ml) || 0; const cost = Number(d.estimated_cost_usd) || 0;
  return (
    <div className="bv-inkchip bv-inkchip--stack" data-testid="ink-chip">
      <div className="bv-inkchip__line">
        {ch.map(([k, v]) => <span key={k} className="bv-inkchip__ch"><i style={{ background: channelDot(k) }} />{k} {v < 0.01 ? '<0.01' : fmt(v)}</span>)}
        <span className="bv-inkchip__sep" />
        <span className="bv-inkchip__total">{`${fmt(ml, 1)} ml`}</span>
        <span className="bv-inkchip__cost">{usd(cost)}</span>
      </div>
      {copies > 1 && <div className="bv-inkchip__extra">{`${copies} copies: ${fmt(ml * copies, 1)} ml · ${usd(cost * copies)}`}</div>}
      {pending.count > 0 && <div className="bv-inkchip__extra">{`In the print queue (${pending.count}): ${fmt(pending.ml, 1)} ml · ${usd(pending.cost)}`}</div>}
    </div>
  );
}

export default function PrintPage() {
  const { t } = useTranslation('bv');
  const S = usePrintStore();
  const { items, setItems, patchItem, activeId, setActiveId, nestMode, setNestMode, nestSelId, setNestSelId, notify, enqueue, jobs, pendingInk } = S;

  /* ---------------------------------------------------------------- computer + printer */
  const { devices, refresh: refreshDevices } = useDevices(15000);
  const { local, permission: localPermission, probed: localProbed } = useLocalComputer(devices, refreshDevices, 15000);   // the Helper on this computer (local check), claimed for this user when new
  const [deviceId, setDeviceId] = useState(() => store.get('bv.print.device'));
  // This computer's Helper wins. Without an answer (older Helper, or the browser refused the local request):
  // the one online computer, else the last used online one; nothing online keeps the last used for the message.
  const device = useMemo(() => {
    if (!devices || !devices.length) return null;
    if (local && local.deviceId) { const mine = devices.find((d) => d.id === local.deviceId); if (mine) return mine; }
    const up = devices.filter((d) => d.online);
    if (up.length === 1) return up[0];
    if (up.length > 1) return up.find((d) => d.id === deviceId) || up[0];
    return devices.find((d) => d.id === deviceId) || devices[0];
  }, [devices, deviceId, local]);
  const online = !!(device && device.online);
  // remember the computer the local check found, so a later hiccup falls back to it, not to another computer
  useEffect(() => {
    if (device && local && device.id === local.deviceId && deviceId !== device.id) { setDeviceId(device.id); store.set('bv.print.device', device.id); }
  }, [device && device.id, local && local.deviceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // what to tell the user about this computer's Helper
  const helperState = !localProbed ? 'checking'
    : local && local.deviceId && devices && devices.some((d) => d.id === local.deviceId) ? 'found'
      : local ? 'otherAccount'
        : localPermission === 'denied' ? 'blocked'
          : localPermission === 'prompt' ? 'prompt' : 'none';
  // the browser's local-network state is only worth a word when no computer is online at all
  const helperNote = (helperState === 'prompt' || helperState === 'blocked') && device && online ? 'found' : helperState;
  const pickerShown = !local && !!devices && devices.filter((d) => d.online).length > 1;   // fallback only: no local answer, several computers online

  // Printer list read from this computer (discover / printer-list). The Bridge's device.printerList is only what the Helper
  // sent when it connected, so a list found later must survive leaving the page (Printables → Print remounts it; testers
  // saw printers "disappear", 2026-10-26): kept per computer in this browser, restored on open, refreshed from the Helper.
  const [found, setFoundState] = useState(null);
  const setFound = useCallback((f) => {
    setFoundState(f);
    if (f && f.deviceId) store.set(`bv.print.found.${f.deviceId}`, JSON.stringify(f.printers || []));
  }, []);
  const [finding, setFinding] = useState(false);
  useEffect(() => {
    if (!device || (found && found.deviceId === device.id)) return;
    try {
      const saved = JSON.parse(store.get(`bv.print.found.${device.id}`) || 'null');
      if (Array.isArray(saved) && saved.length) setFoundState({ deviceId: device.id, printers: saved });
    } catch { /* bad entry: ignore */ }
  }, [device && device.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const printers = useMemo(() => {
    // the Helper's own list (found / restored / refreshed) wins; the connect-time list only until then.
    // Not merged: a printer removed on purpose must not come back from the stale connect-time list.
    const list = (found && found.deviceId === (device && device.id) ? found.printers : device && device.printerList) || [];
    return list.filter((p) => p && p.ip);
  }, [found, device]);
  const [printerIp, setPrinterIp] = useState(() => store.get('bv.print.printer'));
  const printer = printers.find((p) => p.ip === printerIp) || (printerIp === '' ? null : printers[0]) || null;
  const selectPrinter = (ip) => { setPrinterIp(ip); store.set('bv.print.printer', ip); };

  const [info, setInfo] = useState(null);
  const [infoAt, setInfoAt] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [live, setLive] = useState(null);
  const [source, setSourceState] = useState(() => store.get('lastSelectedPaperSource'));
  const setSource = (v) => { setSourceState(v); store.set('lastSelectedPaperSource', v); };
  const sources = useMemo(() => dedupeSources((info && info.ok && info.inputs) || []), [info]);
  const dev = sources.find((d) => sourceValue(d) === source) || sources[0] || null;
  const geom = paperGeom(dev);

  const printerKey = device && printer ? `${device.id}|${printer.ip}` : null;
  const infoSeq = useRef(0);
  const keyRef = useRef(null);
  keyRef.current = printerKey;
  const loadInfo = useCallback(async (force) => {
    if (!device || !printer || !device.online) return false;
    const key = `${device.id}|${printer.ip}`;
    if (key !== keyRef.current) return false;
    const seq = ++infoSeq.current;
    setLoadingInfo(true);
    const [i, l] = await Promise.all([
      runCommand(device.id, 'printer-info', { ip: printer.ip, model: printer.model, ...(force ? { forceRefresh: true } : {}) }, { requireOnline: true, timeoutMs: 210000 }),
      runCommand(device.id, 'printer-live-status', { ip: printer.ip, model: printer.model }, { requireOnline: true, timeoutMs: 70000 }),
    ]);
    if (seq !== infoSeq.current || key !== keyRef.current) return false;
    setLoadingInfo(false);
    const fresh = i.ok ? i.result : { ok: false, error: i.error };
    setInfo((prev) => (fresh.ok || !prev ? fresh : prev));
    setLive(l.ok ? l.result : { ok: false });
    setInfoAt(Date.now());
    if (fresh.ok) store.set(`bv.print.info.${key}`, JSON.stringify({ at: Date.now(), info: { ok: true, inputs: fresh.inputs, inks: fresh.inks } }));
    else if (!force) notify(/unsupported/i.test(fresh.error || '') ? '⚠️ Unsupported printer series' : /timed? ?out|did not answer/i.test(fresh.error || '') ? '⚠️ Printer not responding' : '⚠️ Could not load printer info', 'coral');
    return !!fresh.ok;
  }, [device && device.id, device && device.online, printer && printer.ip, printer && printer.model]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Last known paper + ink for this printer, so a print can be queued while the computer is offline.
    let cached = null;
    try { cached = printerKey && JSON.parse(store.get(`bv.print.info.${printerKey}`) || 'null'); } catch { cached = null; }
    infoSeq.current++; setLoadingInfo(false);
    setInfo(cached ? cached.info : null); setLive(null); setInfoAt(cached ? cached.at : null);
    loadInfo(false);
  }, [printerKey, online]); // eslint-disable-line react-hooks/exhaustive-deps
  // Live status every 30 s, and again after each print ends.
  const pollLive = useCallback(async () => {
    if (!device || !printer || !online) return;
    const l = await runCommand(device.id, 'printer-live-status', { ip: printer.ip, model: printer.model }, { requireOnline: true, timeoutMs: 70000 });
    if (l.ok) setLive(l.result);
  }, [printerKey, online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!printerKey || !online) return undefined;
    const t = setInterval(pollLive, 30000);
    return () => clearInterval(t);
  }, [pollLive]); // eslint-disable-line react-hooks/exhaustive-deps
  const endedCount = jobs.filter((j) => j.endedAt).length;
  useEffect(() => { if (endedCount) { const ts = [1500, 6000, 15000].map((ms) => setTimeout(pollLive, ms)); return () => ts.forEach(clearTimeout); } return undefined; }, [endedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshPrinter = async () => {
    setRefreshing(true);
    pollLive();
    const ok = await loadInfo(true);
    setRefreshing(false);
    notify(ok ? '✓ Printer info refreshed successfully' : '⚠️ Could not refresh printer info', ok ? 'green' : 'coral', 3000);
  };
  const listPrinters = async () => {
    const r = await runCommand(device.id, 'printer-list', { maxAgeMs: null }, { requireOnline: true, timeoutMs: 60000 });
    if (r.ok && Array.isArray(r.result.printers)) setFound({ deviceId: device.id, printers: r.result.printers });
    return r.ok ? r.result.printers || [] : null;
  };
  // On open (and when the computer changes / comes online): ask the Helper for its saved printers, quietly.
  const listedFor = useRef(null);
  useEffect(() => {
    if (!device || !device.online || listedFor.current === device.id) return;
    listedFor.current = device.id;
    listPrinters();
  }, [device && device.id, device && device.online]); // eslint-disable-line react-hooks/exhaustive-deps
  const findPrinters = async () => {
    if (!device) return false;
    setFinding(true);
    const r = await runCommand(device.id, 'discover', { fresh: true }, { requireOnline: true, timeoutMs: 320000 });
    setFinding(false);
    const list = r.ok && Array.isArray(r.result.printers) ? r.result.printers.filter((p) => p && p.ip) : null;
    if (!list) { notify(r.error || t('print.msg.couldNotLook'), 'coral'); return false; }
    setFound({ deviceId: device.id, printers: list });
    if (!list.length) { notify(t('print.msg.noneFoundTryIp')); return false; }
    notify(t('print.msg.found', { count: list.length }), 'green', 3000);
    if (!list.some((p) => p.ip === printerIp) && list.length === 1) selectPrinter(list[0].ip);
    return true;
  };
  const addByIp = async (ip) => {
    const r = await runCommand(device.id, 'probe-ip', { ip, timeout: 5.0 }, { requireOnline: true, timeoutMs: 120000 });
    if (!r.ok || !r.result.printer) { notify(t('print.msg.noneAtIp'), 'coral'); return false; }
    const p = r.result.printer;
    await listPrinters();
    selectPrinter(p.ip);
    if (!p.series || /unknown/i.test(p.series)) notify(t('print.msg.addedUnknown', { name: p.model || ip }));
    else notify(t('print.msg.added', { name: `${p.model} (${p.series})` }), 'green', 3000);
    return true;
  };
  const removePrinter = async (p) => {
    const r = await runCommand(device.id, 'remove-printer', { ip: p.ip }, { requireOnline: true, timeoutMs: 60000 });
    if (!r.ok) { notify(t('print.msg.removeFailed'), 'coral'); return; }
    await listPrinters();
    if (printer && printer.ip === p.ip) selectPrinter('');
    notify(t('print.msg.removed', { name: p.model || p.ip }), 'green', 3000);
  };
  const pill = device && !online
    ? { tone: 'yellow', text: helperState === 'otherAccount' ? t('print.helper.otherAccount') : helperState === 'none' || helperState === 'prompt' || helperState === 'blocked' ? t('print.helper.openOrInstall') : helperState === 'found' ? t('print.helper.reconnecting') : t('print.deviceOffline', { name: device.name }) }
    : statusPill(live, info, dev);

  /* ---------------------------------------------------------------- school (approval) */
  const [school, setSchool] = useState(null);
  useEffect(() => { schoolMe().then((r) => r.ok && setSchool(r)); }, []);
  const needsApproval = !!(school && !school.canPrint);
  const [sendingId, setSendingId] = useState(null);
  const [cardStatus, setCardStatus] = useState({});
  const [jobsVersion, setJobsVersion] = useState(0);

  /* ---------------------------------------------------------------- cards */
  useEffect(() => { const handed = takeHandedFiles(); if (handed.length) S.addFiles(handed); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const active = items.find((f) => f.id === activeId) || items[0] || null;
  const fam = printer ? family(printer.model) || 'T600' : 'T600';
  const lim = customLimits(printer && printer.model, geom);
  const [quality, setQuality] = useState(DEFAULT_QUALITY);
  const [trimmer, setTrimmer] = useState(true);
  const [copies, setCopiesState] = useState(1);
  const setCopies = (n) => setCopiesState(Math.max(1, Math.min(50, Number(n) || 1)));
  const [confirmDel, setConfirmDel] = useState(null);
  const [ask, setAsk] = useState(null); // printer busy / stopped confirm {kind, resolve}
  const canUpload = needsApproval || !!(printer && geom);
  const ready = !!(device && printer && geom && geom.widthIn);

  const onOrientation = (it, v) => {
    if (v === 'custom') {
      if (!geom) { notify(t('print.msg.pickPaperFirst')); return; }
      patchItem(it.id, { orientation: 'custom', draft: { ...defaultCustom(it, printer && printer.model, geom), lock: true }, custom: null });
    } else patchItem(it.id, { orientation: v, custom: null });
  };
  const onGenerate = (it, w, h) => {
    if (!geom || !geom.widthIn) { notify(t('print.msg.pickPaperFirst')); return; }
    if (!(w >= 8)) { notify(t('print.msg.minWidth8')); return; }
    if (!(h >= lim.minH)) { notify(t('print.msg.minHeight', { n: lim.minH })); return; }
    if (h > 60) { notify(t('print.msg.maxHeight60')); return; }
    const c = { w: r2(w), h: r2(h) };
    const err = customError(c, printer && printer.model, geom);
    if (err) { notify(err); return; }
    patchItem(it.id, { custom: c });
    notify(`${c.w}" × ${c.h}" ready`, 'green', 2000);
  };

  // Paper or printer changed: redo what was sized for the old paper (desktop PP:1765-1801).
  const paperKey = geom ? `${geom.widthIn}|${geom.isRoll}|${geom.minMarginIn}|${fam}` : null;
  const lastPaper = useRef(null);
  useEffect(() => {
    if (!paperKey) return;
    const prev = lastPaper.current;
    lastPaper.current = paperKey;
    if (!prev || prev === paperKey) return;
    if (nestMode) {
      if (!geom.isRoll || !(stripWidthIn > 0)) { exitNest(); notify(t('print.nest.needRollAndTwo')); } else arrange('tight');
    }
    const printable = geom.widthIn ? geom.widthIn - 2 * (geom.minMarginIn || 0) : null;
    setItems((xs) => xs.map((it) => {
      if (it.orientation !== 'custom') return it;
      if (!geom.isRoll || !printable) return { ...it, orientation: 'portrait', custom: null };
      if (!it.custom) return it;
      const w = Math.min(it.custom.w, printable);
      const h = it.draft && it.draft.lock ? r2(it.custom.h * (w / it.custom.w)) : it.custom.h;
      return { ...it, custom: { w: r2(w), h }, draft: { ...it.draft, w: r2(w), h } };
    }));
  }, [paperKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------------------------------------------- nesting */
  const rollWidthIn = (geom && geom.widthIn) || (geom && geom.isRoll && (fam === 'T600' || fam === 'T500') ? modelRollWidthIn(printer && printer.model) : null) || null;
  const stripWidthIn = rollWidthIn ? stripWidthFor(fam, rollWidthIn) : 0;
  const aspectOf = (it) => (it && it.w > 0 && it.h > 0 ? it.h / it.w : 1);
  const placedAsp = (it) => placedAspect({ aspect: aspectOf(it), rotated: !!it.nestRotated });
  const boxOf = (it) => ({ id: it.id, x: it.nestX, y: it.nestY, w: it.nestW, h: it.nestH, z: it.nestZ || 0, rotated: !!it.nestRotated });
  const placed = items.filter((it) => it.nestPlaced);
  const tilesBoxes = placed.map(boxOf);
  const stripHeightIn = stripHeightOf(tilesBoxes);
  const fillPct = nestMode && tilesBoxes.length && stripWidthIn ? Math.round(visibleFill(tilesBoxes, stripWidthIn, stripHeightIn) * 100) : 0;
  const hasRoll = !!rollWidthIn && !!(geom && geom.isRoll);
  const canNest = items.length >= 2 && hasRoll;
  const nestBlocked = items.length < 2 && !hasRoll ? t('print.nest.blockedBoth')
    : items.length < 2 ? t('print.nest.blockedTwo') : !hasRoll ? t('print.nest.blockedRoll') : null;
  const printable = nestMode && stripWidthIn ? printOrder(tilesBoxes).filter((p) => p.x < stripWidthIn && p.x + p.w > 0 && p.y + p.h > 0) : [];

  const defaultBox = (it, list) => {
    const w = Math.min(stripWidthIn || NEST_AUTO_MIN_IN, (autoEqualWidths(list.map((x) => ({ id: x.id })), stripWidthIn)[0] || {}).widthIn || NEST_AUTO_MIN_IN);
    return { w: r3(w), h: r3(w * placedAsp(it)) };
  };
  const topZ = (list) => Math.max(0, ...list.filter((x) => x.nestPlaced).map((x) => x.nestZ || 0));
  const selectTile = (id) => { setNestSelId(id); if (id != null) setActiveId(id); };
  const isOffPageBox = (b) => b.x < -0.005 || b.y < -0.005 || b.x + b.w > stripWidthIn + 0.005;
  const warnOff = (b) => { if (b && isOffPageBox(b)) notify(t('print.nest.offPage'), 'yellow', 3500); };
  const tileChange = (id, p) => setItems((xs) => xs.map((it) => {
    if (it.id !== id || !it.nestPlaced) return it;
    const n = { ...it };
    if (p.x != null) n.nestX = p.x;
    if (p.y != null) n.nestY = p.y;
    if (p.w != null) n.nestW = Math.max(NEST_MIN_ITEM_IN, p.w);
    if (p.h != null) n.nestH = Math.max(NEST_MIN_ITEM_IN, p.h);
    if (p.z != null) n.nestZ = p.z;
    return n;
  }));
  const placeItem = (id, at = null) => {
    setItems((xs) => {
      const it = xs.find((x) => x.id === id);
      if (!it || it.nestPlaced) return xs;
      const { w, h } = defaultBox(it, xs);
      const pos = at ? { x: r3(at.x - w / 2), y: r3(at.y - h / 2) } : findFreeSpot(xs.filter((x) => x.nestPlaced).map(boxOf), w, h, stripWidthIn);
      return xs.map((x) => (x.id === id ? { ...x, nestPlaced: true, nestX: pos.x, nestY: pos.y, nestW: w, nestH: h, nestZ: topZ(xs) + 1, nestRotated: false } : x));
    });
    selectTile(id);
  };
  const onNestDrop = (id, xIn, yIn) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    if (it.nestPlaced) {
      tileChange(id, { x: r3(xIn - it.nestW / 2), y: r3(yIn - it.nestH / 2), z: topZ(items) + 1 });
      selectTile(id);
      warnOff({ x: xIn - it.nestW / 2, y: yIn - it.nestH / 2, w: it.nestW, h: it.nestH });
      return;
    }
    const { w, h } = defaultBox(it, items);
    placeItem(id, { x: xIn, y: yIn });
    warnOff({ x: xIn - w / 2, y: yIn - h / 2, w, h });
  };
  const addAll = () => setItems((xs) => {
    let next = xs;
    for (const it of xs) {
      if (it.nestPlaced) continue;
      const { w, h } = defaultBox(it, xs);
      const pos = findFreeSpot(next.filter((x) => x.nestPlaced).map(boxOf), w, h, stripWidthIn);
      const z = topZ(next) + 1;
      next = next.map((x) => (x.id === it.id ? { ...x, nestPlaced: true, nestX: pos.x, nestY: pos.y, nestW: w, nestH: h, nestZ: z, nestRotated: false } : x));
    }
    return next;
  });
  const removeTile = (id) => { setItems((xs) => xs.map((it) => (it.id === id ? clearNest(it) : it))); if (nestSelId === id) setNestSelId(null); };
  function arrange(kind) {
    setItems((xs) => {
      const pl = xs.filter((it) => it.nestPlaced);
      if (!pl.length) return xs;
      const boxes = kind === 'equal'
        ? arrangeEqual(pl.map((it) => ({ id: it.id, aspect: aspectOf(it), rotated: !!it.nestRotated })), stripWidthIn)
        : arrangeTight(pl.map((it) => ({ id: it.id, w: it.nestW, h: it.nestH })), stripWidthIn);
      const byId = new Map(boxes.map((b) => [b.id, b]));
      return xs.map((it) => { const b = byId.get(it.id); return b ? { ...it, nestX: b.x, nestY: b.y, nestW: b.w, nestH: b.h } : it; });
    });
  }
  function exitNest() { setNestMode(false); setNestSelId(null); setItems((xs) => xs.map(clearNest)); }
  const enterNest = (id) => {
    if (!canNest) { notify(t('print.nest.needRollAndTwo')); return; }
    // Custom cards nest their ORIGINAL image (desktop: back to portrait).
    const restored = items.map((it) => ({ ...clearNest(it), ...(it.orientation === 'custom' ? { orientation: 'portrait', custom: null } : {}) }));
    setItems(restored.map((it) => {
      if (it.id !== id) return it;
      const { w, h } = defaultBox(it, restored);
      return { ...it, nestPlaced: true, nestX: 0, nestY: 0, nestW: w, nestH: h, nestZ: 1, nestRotated: false };
    }));
    setNestSelId(id); setActiveId(id);
    setNestMode(true);
  };
  const selItem = nestSelId != null ? placed.find((it) => it.id === nestSelId) || null : null;
  const setSelSize = (dim, v) => {
    if (!selItem || !Number.isFinite(v) || v <= 0) return;
    if (v < NEST_MIN_ITEM_IN) notify(t('print.nest.minSide', { n: NEST_MIN_ITEM_IN }));
    tileChange(selItem.id, { [dim]: Math.max(NEST_MIN_ITEM_IN, r3(v)) });
  };
  const setSelOrientation = (o) => setItems((xs) => xs.map((it) => {
    if (!selItem || it.id !== selItem.id || !it.nestPlaced) return it;
    const rotated = (o === 'landscape') !== (aspectOf(it) < 1);
    if (!!it.nestRotated === rotated) return it;
    const cx = it.nestX + it.nestW / 2; const cy = it.nestY + it.nestH / 2;
    return { ...it, nestRotated: rotated, nestW: it.nestH, nestH: it.nestW, nestX: r3(cx - it.nestH / 2), nestY: r3(Math.max(0, cy - it.nestW / 2)) };
  }));

  // Sharp tile pictures (≤ 2048 px), decoded once per card; the card picture stands in meanwhile.
  const bitmaps = useRef(new Map());
  const [, setBmpTick] = useState(0);
  useEffect(() => {
    if (!nestMode) return;
    for (const it of items) {
      if (bitmaps.current.has(it.id)) continue;
      bitmaps.current.set(it.id, null);
      (async () => {
        const blob = it.raw || await (await fetch(it.url)).blob();
        const s = Math.min(1, 2048 / Math.max(it.w || 1, it.h || 1));
        const bmp = await createImageBitmap(blob, s < 1 ? { resizeWidth: Math.round(it.w * s), resizeHeight: Math.round(it.h * s), resizeQuality: 'high' } : {});
        bitmaps.current.set(it.id, bmp);
        setBmpTick((n) => n + 1);
      })().catch(() => {});
    }
  }, [nestMode, items]);
  const tiles = placed.map((it) => ({ ...boxOf(it), num: items.indexOf(it) + 1, src: bitmaps.current.get(it.id) })).filter((t) => t.src);

  // Nesting takes over the right panel; the boxes go back when it ends (transient, not saved).
  const [printersOpen, setPrintersOpen, setPrintersOpenT] = usePersistedOpen('pp.printersBoxOpen');
  const [inkOpen, setInkOpen, setInkOpenT] = usePersistedOpen('pp.inkBoxOpen');
  const [settingsOpen, setSettingsOpen, setSettingsOpenT] = usePersistedOpen('pp.printSettingsBoxOpen');
  const [jobsOpen, setJobsOpen, setJobsOpenT] = usePersistedOpen('pp.printJobsBoxOpen');
  // Product tour (desktop pse-open-printers-box / pse-open-ink-box): a step pointing into a box opens it first.
  useEffect(() => {
    const p = () => setPrintersOpen(true);
    const i = () => setInkOpen(true);
    window.addEventListener('pse-open-printers-box', p);
    window.addEventListener('pse-open-ink-box', i);
    return () => { window.removeEventListener('pse-open-printers-box', p); window.removeEventListener('pse-open-ink-box', i); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [copiesOpen, setCopiesOpen] = useState(false);
  const [nestOpen, setNestOpen] = useState(false);
  const [panelMin, setPanelMin] = useState(false);
  const snap = useRef(null);
  useEffect(() => {
    if (nestMode && !snap.current) {
      snap.current = { printersOpen, inkOpen, settingsOpen, jobsOpen };
      setPrintersOpenT(false); setInkOpenT(false); setJobsOpenT(false); setSettingsOpenT(true); setNestOpen(true); setPanelMin(false);
    } else if (!nestMode && snap.current) {
      const s = snap.current; snap.current = null;
      setPrintersOpenT(s.printersOpen); setInkOpenT(s.inkOpen); setJobsOpenT(s.jobsOpen); setSettingsOpenT(s.settingsOpen); setNestOpen(false);
    }
  }, [nestMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // A printer turning to an error opens the Printers box (desktop).
  const pillTone = pill && pill.tone;
  useEffect(() => { if (pillTone === 'coral') setPrintersOpenT(true); }, [pillTone]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------------------------------------------- ink estimate */
  const [est, setEst] = useState(null);
  const size = printSize(active && active.orientation === 'custom' && !active.custom ? { ...active, orientation: 'portrait' } : active, geom);
  const nestSig = nestMode && printable.length && device && online && printer ? JSON.stringify([printable.map((p) => [p.id, p.x, p.y, p.w, p.h, p.rotated]), stripWidthIn, stripHeightIn, geom && geom.paper, quality, printer.model]) : null;
  const singleSig = !nestMode && active && active.key && size && device && online && printer ? [active.estimateKey || active.key, size.widthIn.toFixed(2), size.heightIn.toFixed(2), geom.paper, quality, printer.model].join('|') : null;
  const estSig = nestSig || singleSig;
  /** One estimate-ink run on the Helper → {data} | {unsupported} | {error}. */
  const runEstimate = async ({ key, wIn, hIn }) => {
    const r = await runCommand(device.id, 'estimate-ink', {
      imagePath: '$image', widthInches: Number(wIn.toFixed(3)), heightInches: Number(hIn.toFixed(3)),
      material: (geom && geom.paper) || 'satin_photo', quality: estimatorQuality(quality), printerModel: printer.model,
    }, { imageKey: key, requireOnline: true, timeoutMs: 180000 });
    if (r.ok) return { data: r.result.data || r.result };
    return r.code === 'UNSUPPORTED' ? { unsupported: true } : { error: r.error };
  };
  /** The whole printable nest strip at 40 dpi, uploaded (desktop nest estimate) → {key, wIn, hIn}. */
  const nestEstimateInput = async () => {
    const byId = new Map(items.map((x) => [x.id, x]));
    const c = await composeNest({ family: fam, stripWidthIn, stripHeightIn, dpi: 40, items: printable.map((p) => { const it = byId.get(p.id); return { src: it.raw || it.url, aspect: aspectOf(it), x: p.x, y: p.y, w: p.w, h: p.h, rotated: p.rotated }; }) });
    const up = await uploadFile(c.file);
    if (!up.ok) throw new Error(up.error);
    return { key: up.key, wIn: stripWidthIn, hIn: stripHeightIn };
  };
  // The preview's run, kept so Print can reuse it (inkForPrint) instead of printing without an estimate.
  const estRun = useRef(null);
  useEffect(() => {
    if (!estSig) { setEst(null); estRun.current = null; return undefined; }
    let gone = false;
    setEst({ loading: true });
    let release;
    const promise = new Promise((resolve) => { release = resolve; });
    estRun.current = { sig: estSig, promise };
    const t = setTimeout(async () => {
      let res;
      try {
        const input = nestSig ? await nestEstimateInput() : { key: active && (active.estimateKey || active.key), wIn: size && size.widthIn, hIn: size && size.heightIn };
        res = await runEstimate(input);
      } catch (e) { res = { error: String(e.message || e) }; }
      release(res);
      if (!gone) setEst(res);
    }, nestSig ? 500 : 600);
    return () => { gone = true; clearTimeout(t); release(null); };
  }, [estSig]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Every print records its ink (print_jobs ml + cost, RedCircle /inks usage) whatever the source — upload,
   * Canva, printable, library, nest. The preview's estimate when it is for exactly this print; else (not
   * finished, failed, or for another card) a fresh run now, retried once. Capped so a slow estimate never
   * holds a print for long. Families with no estimator model (T200, T500, Canon TM) return null.
   */
  const INK_WAIT_MS = 90000;
  const inkForPrint = async (sig, input) => {
    const capped = (p) => Promise.race([p, new Promise((r) => setTimeout(() => r({ error: 'timeout' }), INK_WAIT_MS))]);
    const fresh = () => capped(Promise.resolve().then(input).then(runEstimate)).catch((e) => ({ error: String(e.message || e) }));
    const cur = estRun.current;
    let res = cur && cur.sig === sig ? await capped(cur.promise) : null;
    if (!res || res.error) res = await fresh();
    if (res && res.error && res.error !== 'timeout') res = await fresh();
    return res && res.data ? { ml: Number(res.data.total_ml) || 0, cost: Number(res.data.estimated_cost_usd) || 0, channels: res.data.per_channel_ml || null } : null;
  };

  /* ---------------------------------------------------------------- printing */
  const queueIdle = !jobs.some((j) => ['queued', 'preparing', 'sending', 'running'].includes(j.status));
  /** desktop confirmPrinterNotBusy: only when our own queue is idle. */
  const confirmNotBusy = async () => {
    if (!queueIdle || !online) return true;
    const b = await runCommand(device.id, 'printer-busy-state', { ip: printer.ip, model: printer.model }, { requireOnline: true, timeoutMs: 60000 });
    const s = b.ok ? b.result : null;
    const kind = s && (s.state === 5 || s.acceptingJobs === false) ? 'stopped' : s && (s.state === 4 || s.queuedJobs > 0) ? 'busy' : null;
    if (!kind) return true;
    return new Promise((resolve) => setAsk({ kind, resolve }));
  };
  const [checking, setChecking] = useState(false);
  const takeOff = (ids) => {
    // The printed cards leave the strip now; they come back if the job is cancelled or skipped.
    setItems((xs) => {
      const rest = xs.filter((x) => !ids.includes(x.id));
      setActiveId(rest[0] ? rest[0].id : null);
      return rest;
    });
  };

  const handlePrint = async (it) => {
    if (!it) { setCardStatus((s) => ({ ...s, [active && active.id]: t('print.msg.chooseImageFirst') })); return; }
    if (!ready) return;
    if (!it.key) { notify(`${it.name} is still uploading.`); return; }
    const file = it.orientation === 'custom' && !it.custom ? { ...it, orientation: 'portrait' } : it;
    const sz = printSize(file, geom);
    setChecking(true);
    const go = await confirmNotBusy();
    const estKey = it.estimateKey || it.key;
    const ink = go && sz ? await inkForPrint([estKey, sz.widthIn.toFixed(2), sz.heightIn.toFixed(2), geom.paper, quality, printer.model].join('|'),
      () => ({ key: estKey, wIn: sz.widthIn, hIn: sz.heightIn })) : null;
    setChecking(false);
    if (!go) return;
    const payload = { ...printPayload({ printer, geom, file, quality, copies: 1 }), verticalTrimmer: trimmer, copies: 1 };
    const custom = payload.isCustomSize;
    const prepare = custom && !sizesOriginal(printer.model) ? async () => {
      // These families print the padded roll-width canvas (desktop createCustomSizedImage), made here.
      const src = it.raw || await (await fetch(it.sourceUrl)).blob();
      const up = await uploadFile(await customCanvas(src, it.custom, geom.widthIn));
      if (!up.ok) throw new Error(up.error);
      return { key: up.key };
    } : null;
    enqueue({
      kind: 'single', name: it.name, thumb: it.url, deviceId: device.id, deviceName: device.name, printer: { ip: printer.ip, model: printer.model },
      payload, key: it.key, prepare, restore: [clearNest(it)], ink,
      school: it.schoolJobId ? { jobId: it.schoolJobId } : null,
      // Desktop IIG Phase 5: a printed catalog / AI poster asks for a one-tap rating (not approval jobs).
      ratingPosterId: !it.schoolJobId && it.posterId ? it.posterId : null,
      report: {
        printerName: printer.model, printerModel: printer.model, printerIp: printer.ip, paperType: geom.paper,
        printWidthInches: sz ? r2(sz.widthIn) : null, printHeightInches: sz ? r2(sz.heightIn) : null,
        orientation: custom ? 'custom' : file.orientation, quality, pipeline: 'brightivity-helper',
        inkEstimateMl: ink ? ink.ml : null, inkEstimateCostUsd: ink ? ink.cost : null, inkChannels: ink ? ink.channels : null,
      },
    }, copies);
    takeOff([it.id]);
    setCopies(1);
  };

  const printNest = async () => {
    if (!ready || !printable.length || !(stripHeightIn > 0)) return;
    setChecking(true);
    const go = await confirmNotBusy();
    const ink = go ? await inkForPrint(nestSig, nestEstimateInput) : null;
    setChecking(false);
    if (!go) return;
    const byId = new Map(items.map((x) => [x.id, x]));
    const its = printable.map((p) => byId.get(p.id));
    const printSrc = printable.map((p) => { const it = byId.get(p.id); return { src: it.raw || it.sourceUrl || it.url, aspect: aspectOf(it), x: p.x, y: p.y, w: p.w, h: p.h, rotated: p.rotated }; });
    const thumb = await nestThumbnail({ stripWidthIn, stripHeightIn, items: printSrc.map((x, i) => ({ ...x, src: its[i].raw || its[i].url })) }).catch(() => null);
    const spec = { family: fam, stripWidthIn, stripHeightIn, items: printSrc };
    enqueue({
      kind: 'nest', name: t('print.nest.jobName', { count: its.length }), count: its.length, thumb, deviceId: device.id, deviceName: device.name,
      printer: { ip: printer.ip, model: printer.model }, ink, restore: its.map(clearNest),
      // print_jobs fields (the Bridge writes the row from bvReport; single prints carry the same `report`).
      report: {
        printerName: printer.model, printerModel: printer.model, printerIp: printer.ip, paperType: geom.paper,
        printWidthInches: r2(stripWidthIn), printHeightInches: r2(stripHeightIn), orientation: 'custom', quality, pipeline: 'brightivity-helper',
        inkEstimateMl: ink ? ink.ml : null, inkEstimateCostUsd: ink ? ink.cost : null, inkChannels: ink ? ink.channels : null,
      },
      payload: {
        ip: printer.ip, model: printer.model, mediaId: Number(geom.mediaId || 1100), widthIn: Number(rollWidthIn), minMarginIn: 0,
        paperLabel: geom.label, paperName: geom.paper, mode: 'portrait', rollIndex: geom.rollIndex, isRoll: geom.isRoll, isCustomSize: true,
        quality: Number(quality), verticalTrimmer: trimmer, copies: 1, customOriginalPath: '$image', imagePath: '$image', clientPlatform: 'web',
      },
      // Compose the previewed placements into ONE strip right before the send (desktop composeNest).
      prepare: async () => {
        const c = await composeNest(spec);
        const up = await uploadFile(c.file);
        if (!up.ok) throw new Error(up.error || t('print.nest.composeFailed'));
        return { key: up.key, payload: { customWidthIn: Number(c.pageWidthIn), customHeightIn: Number(c.pageHeightIn) } };
      },
    }, copies);
    setNestMode(false); setNestSelId(null);
    setItems((xs) => xs.filter((x) => !printable.some((p) => p.id === x.id)).map(clearNest));
    setActiveId(null);
    setCopies(1);
  };

  const sendForApproval = async (f) => {
    if (!f) return;
    if (!printer || !geom) { setCardStatus((s) => ({ ...s, [f.id]: t('print.msg.pickPrinterPaper') })); return; }
    setSendingId(f.id);
    setCardStatus((s) => ({ ...s, [f.id]: t('print.approval.sending') }));
    let imageKey; let thumbKey = null; let bytes = null;
    const fail = (e) => { setSendingId(null); setCardStatus((s) => ({ ...s, [f.id]: e || t('print.approval.failed') })); };
    if (f.raw) {
      setCardStatus((s) => ({ ...s, [f.id]: t('print.approval.uploading') }));
      const up = await uploadForApproval(f.raw);
      if (!up.ok) return fail(up.error);
      imageKey = up.key; bytes = f.raw.size;
      try { const t = await makeThumbnail(f.raw); const tu = await uploadForApproval(t.file, 'thumb'); if (tu.ok) thumbKey = tu.key; } catch { /* thumbnail is optional */ }
    } else {
      const ing = await ingestForApproval(f.sourceUrl, f.url);
      if (!ing.ok) return fail(ing.error);
      imageKey = ing.imageKey; thumbKey = ing.thumbKey || null; bytes = ing.sizeBytes || null;
    }
    const file = f.orientation === 'custom' && !f.custom ? { ...f, orientation: 'portrait' } : f;
    const pl = printPayload({ printer, geom, file, quality, copies });
    const { imagePath: _ip, customOriginalPath: _co, ...settings } = pl;
    const sz = printSize(file, geom);
    const r = await submitForApproval({
      imageKey, thumbKey, originalKey: pl.isCustomSize ? imageKey : undefined, imageSizeBytes: bytes, designName: f.name,
      printSettings: { ...settings, verticalTrimmer: trimmer, heightIn: sz ? r2(sz.heightIn) : undefined, printWidthIn: sz ? r2(sz.widthIn) : undefined },
      requestedPrinter: { ip: printer.ip, model: printer.model, deviceId: device && device.id, computer: device && device.name },
      inkEstimateMl: est && est.data ? est.data.total_ml : undefined, inkEstimateCostUsd: est && est.data ? est.data.estimated_cost_usd : undefined,
    });
    if (!r.ok) return fail(r.error);
    setSendingId(null);
    setCardStatus((s) => ({ ...s, [f.id]: t('print.approval.sent') }));
    notify(t('print.approval.sentTo', { who: school.schoolName || t('print.approval.yourAdmin') }), 'green');
    setJobsVersion((v) => v + 1);
  };

  /* ---------------------------------------------------------------- render */
  const noDevices = devices && devices.length === 0;
  const unplaced = items.filter((it) => !it.nestPlaced).length;
  const nest = {
    mode: nestMode, count: placed.length, fillPct, blocked: nestBlocked, unplaced, printableCount: printable.length,
    stripWidthIn, stripHeightIn, canPrint: ready && !checking,
    selItem: selItem ? {
      id: selItem.id, num: items.indexOf(selItem) + 1, w: selItem.nestW, h: selItem.nestH,
      cropped: Math.abs(selItem.nestH - selItem.nestW * placedAsp(selItem)) > 0.01, orientation: placedAsp(selItem) < 1 ? 'landscape' : 'portrait',
    } : null,
    exit: exitNest, addAll, arrange, remove: removeTile, setSize: setSelSize, setOrientation: setSelOrientation,
    uncrop: () => selItem && tileChange(selItem.id, { h: r3(selItem.nestW * placedAsp(selItem)) }), print: printNest,
  };
  const showTrimmer = active && active.orientation === 'custom' && fam === 'Z6Z9' && printer;
  const printLabel = copies > 1 ? `${t('print.print')} ×${copies}` : t('print.print');

  return (
    <div className="bv-content bv-printpage">
      <TopBar title={t('print.title')} subtitle={t('print.subtitle')} />
      {noDevices ? (
        <Card><EmptyState icon="print" title={t('print.noComputer.title')}
          action={<Button as="a" href={helperDownloadUrl(browserOs())} variant="blue" icon="upload" data-testid="helper-download">{t('print.noComputer.action', { os: browserOs() === 'mac' ? 'Mac' : 'Windows' })}</Button>}>
          {t('print.noComputer.body')} <Link to="/settings#computers">{t('print.noComputer.more')}</Link>
        </EmptyState></Card>
      ) : (
        <div className={`bv-pp${panelMin ? ' is-panel-min' : ''}`}>
          {/* ---------------- left: Print Queue */}
          <aside className="bv-pp__queue" aria-label={t('print.queue')} data-tour="print-dropzone">
            <h2 className="bv-pp__title">{t('print.queue')}</h2>
            <div className="bv-label">{t('print.upload')}</div>
            <DropZone onFiles={S.addFiles} disabled={!canUpload} />
            {S.importing && <p className="bv-small bv-muted" role="status" data-testid="pdf-importing">{S.importing.total ? t('print.readingPage', { name: S.importing.name, n: S.importing.n, total: S.importing.total }) : t('print.reading', { name: S.importing.name })}</p>}
            {nestMode ? (
              <div className="bv-nthumbs" data-testid="nest-thumbs">
                {items.map((it, i) => <NestThumb key={it.id} item={it} num={i + 1} placed={!!it.nestPlaced} selected={nestSelId === it.id}
                  onClick={() => (it.nestPlaced ? selectTile(it.id) : placeItem(it.id))} />)}
              </div>
            ) : (
              <div className="bv-itemlist" id="file-card-section">
                {items.map((it) => (
                  <ItemCard key={it.id} item={it} active={active && it.id === active.id} busy={false}
                    canPrint={!needsApproval || !!it.schoolJobId} sendJob={needsApproval && !it.schoolJobId} sending={sendingId === it.id}
                    printEnabled={ready && !!it.key && !checking} printLabel={checking && active && it.id === active.id ? t('print.checking') : printLabel}
                    onActivate={() => setActiveId(it.id)} onOrientation={(v) => onOrientation(it, v)}
                    onDraft={(p) => patchItem(it.id, (x) => ({ draft: { ...x.draft, ...p } }))} onGenerate={(w, h) => onGenerate(it, w, h)}
                    onPrint={() => (needsApproval && !it.schoolJobId ? sendForApproval(it) : handlePrint(it))}
                    showNest={!needsApproval && items.length >= 2} nestEnabled={canNest} onNest={() => enterNest(it.id)}
                    onDelete={() => setConfirmDel(it)} status={cardStatus[it.id] || (it.error ? it.error : null)}
                    customAllowed={!geom || geom.isRoll} minH={lim.minH} />
                ))}
              </div>
            )}
          </aside>

          {/* ---------------- centre */}
          <main className="bv-pp__centre" data-tour="print-preview">
            <div className="bv-pp__controls">
              <label className="bv-printbar__label" htmlFor="quality-select">{t('print.quality')}</label>
              <Select id="quality-select" className="bv-printbar__quality" value={String(quality)} onChange={(e) => setQuality(Number(e.target.value))} options={QUALITY.map((o) => ({ ...o, label: t(o.label) }))} />
              {showTrimmer && (
                <>
                  <label className="bv-printbar__label" htmlFor="trimmer-select">{t('print.trimmer')}</label>
                  <Select id="trimmer-select" className="bv-printbar__trim" value={trimmer ? 'on' : 'off'} onChange={(e) => setTrimmer(e.target.value === 'on')}
                    options={[{ value: 'on', label: t('print.trimmerOn') }, { value: 'off', label: t('print.trimmerOff') }]} />
                </>
              )}
              {geom && (active || nestMode) && <InkPill est={est} copies={copies} pending={pendingInk} />}
            </div>
            {nestMode && stripWidthIn > 0 ? (
              <div className="bv-pp__nest">
                <NestCanvas tiles={tiles} rollWidthIn={rollWidthIn} stripWidthIn={stripWidthIn} activeId={nestSelId} busy={false}
                  onSelect={selectTile} onChange={tileChange} onDropItem={onNestDrop} onRemove={removeTile}
                  onOffPageRelease={() => notify(t('print.nest.offPage'), 'yellow', 3500)} />
                <p className="bv-pv__caption">
                  <b>{placed.length ? `${t('print.nest.nest')} · ${t('print.images', { count: placed.length })}` : t('print.nest.nesting')}</b>
                  {placed.length > 0 && ` ${t('print.nest.ofRoll', { w: fmt(stripWidthIn, 1), h: fmt(stripHeightIn, 1), pct: fillPct })}`}
                </p>
              </div>
            ) : (
              <Preview item={active && active.orientation === 'custom' && !active.custom ? { ...active, orientation: 'portrait' } : active} size={size} geom={geom} />
            )}
          </main>

          {/* ---------------- right panel */}
          <aside className="bv-pp__panel" id="rightpanel" aria-label={t('print.panelAria')}>
            <div className="bv-pp__panelhead">
              <button type="button" className="bv-roundbtn bv-roundbtn--sm" id="rightpanel-minimize-btn" title={t('print.minimizePanel')} aria-label={t('print.minimizePanel')} onClick={() => setPanelMin(true)}><Icon name="arrow" size={16} /></button>
            </div>
            <PrintersBox open={printersOpen} setOpen={setPrintersOpen} devices={devices} device={device} helperState={helperNote} pickerShown={pickerShown} localDevice={!!(local && device && local.deviceId === device.id)} setDevice={(id) => { setDeviceId(id); store.set('bv.print.device', id); }}
              printers={printers} printer={printer} selectPrinter={selectPrinter} info={info} infoAt={infoAt} loadingInfo={loadingInfo} refreshing={refreshing}
              onRefresh={refreshPrinter} sources={sources} source={dev ? sourceValue(dev) : ''} setSource={setSource} pill={pill}
              finding={finding} onFind={findPrinters} onAddIp={addByIp} onRemovePrinter={removePrinter} notify={notify} />
            {needsApproval && <PrintJobsBox open={jobsOpen} setOpen={setJobsOpen} version={jobsVersion} />}
            {!needsApproval && (
              <PrintSettingsBox open={settingsOpen} setOpen={setSettingsOpen} copies={copies} setCopies={setCopies} nest={nest}
                copiesOpen={copiesOpen} setCopiesOpen={setCopiesOpen} nestOpen={nestOpen} setNestOpen={setNestOpen} />
            )}
            <InkBox open={inkOpen} setOpen={setInkOpen} info={info} loading={loadingInfo} hasPrinter={!!printer} supplyUrl={SUPPLY_STORE_URL} />
          </aside>
          {panelMin && <button type="button" className="bv-pp__restore" id="rightpanel-restore-btn" title={t('print.restorePanel')} aria-label={t('print.restorePanel')} onClick={() => setPanelMin(false)}><Icon name="arrow" size={18} /></button>}
        </div>
      )}

      <Modal open={!!ask} title={t('print.busy.title')} onClose={() => { ask && ask.resolve(false); setAsk(null); }}>
        <p className="bv-muted">{ask && ask.kind === 'stopped'
          ? t('print.busy.stopped')
          : t('print.busy.busy')}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => { ask.resolve(false); setAsk(null); }}>{t('common.cancel')}</Button>
          <Button variant="coral" icon="print" onClick={() => { ask.resolve(true); setAsk(null); }}>{t('print.busy.anyway')}</Button>
        </div>
      </Modal>
      <Modal open={!!confirmDel} title={t('print.delete.title')} onClose={() => setConfirmDel(null)}>
        <p className="bv-muted">{confirmDel && t('print.delete.sure', { name: confirmDel.name })}</p>
        <p className="bv-muted">{t('common.cannotUndo')}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirmDel(null)}>{t('common.cancel')}</Button>
          <Button variant="coral" icon="trash" onClick={() => { const f = confirmDel; setConfirmDel(null); S.removeItem(f.id); }}>{t('common.delete')}</Button>
        </div>
      </Modal>
    </div>
  );
}
